import { useTeamCatalog, withTeamCatalog } from '@context/TeamCatalog.context'
import React, { useEffect, useState } from 'react'
import './AddDeviceStyle.css'
import useAddDevice from './useAddDevice'
import useCommunicationsLeaveGuard from '../../shared/useCommunicationsLeaveGuard'
import UnsavedChangesModal from '../../UnsavedChangesModal/UnsavedChangesModal.component'
import { CallRecordingMode, IClinicPhoneDevice } from '@dataTypes/clinic.type'
import VoipConfigurationModal from './VoipConfigurationModal'
import E911AddressModal from './E911AddressModal'
import { getFullAddress } from '@utils/address.util'
import { callRecordingModeFromBooleans } from '@utils/clinic.util'
import {
  isSettingFieldChanged,
  settingsFieldClassName,
} from '@utils/settings.util'
import {
  ActionButtons,
  Badge,
  Breadcrumbs,
  Button,
  Combobox,
  DataTable,
  EmptyState,
  InputField,
  Modal,
  MultiselectCombobox,
  Select,
  type BadgeColor,
  type DataTableColumn,
  type DataTableSortDirection,
} from '@enamelai/design-system'
import { Icon as Delete2Icon } from '@enamelai/design-system/icons/Delete2'
import { Icon as EditIcon } from '@enamelai/design-system/icons/Edit'
import { Icon as InfoIcon } from '@enamelai/design-system/icons/Info'
import { Icon as LocationIcon } from '@enamelai/design-system/icons/Location'

const DEVICE_TYPES: { id: IClinicPhoneDevice['device_type']; text: string }[] =
  [
    { id: 'soft_phone', text: 'Soft phone' },
    { id: 'desk_phone', text: 'Desk phone' },
    { id: 'voip_phone', text: 'VoIP phone' },
    { id: 'mobile_phone', text: 'Mobile phone' },
  ]

type CallRecordingDirection = 'inbound' | 'outbound'

const CALL_RECORDING_OPTIONS: { id: CallRecordingDirection; text: string }[] = [
  { id: 'inbound', text: 'Inbound' },
  { id: 'outbound', text: 'Outbound' },
]

const callRecordingModeToValues = (
  mode: CallRecordingMode,
): CallRecordingDirection[] => {
  switch (mode) {
    case 'both':
      return ['inbound', 'outbound']
    case 'inbound':
      return ['inbound']
    case 'outbound':
      return ['outbound']
    default:
      return []
  }
}

const valuesToCallRecordingMode = (
  values: ReadonlyArray<string> | null | undefined,
): CallRecordingMode => {
  const selected = new Set(values ?? [])
  const inbound = selected.has('inbound')
  const outbound = selected.has('outbound')
  if (inbound && outbound) return 'both'
  if (inbound) return 'inbound'
  if (outbound) return 'outbound'
  return 'none'
}

const E911_STATUS_COLOR: Record<string, BadgeColor> = {
  pending: 'yellow',
  verified: 'vivid-green',
  failed: 'red',
}

const selectString = (value: string | string[]): string =>
  typeof value === 'string' ? value : (value[0] ?? '')

type E911TableRow = { id: string }

type E911SortColumn = 'address' | 'is_default' | 'status'

const DEFAULT_PAGE_SIZE = 10

const useSortedPage = <T extends string>(): {
  currentPage: number
  pageSize: number
  sortColumnId: T | null
  sortDirection: DataTableSortDirection
  handleSort: (columnId: string) => void
  setCurrentPage: (page: number) => void
  onPageSizeChange: (size: number) => void
} => {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sortColumnId, setSortColumnId] = useState<T | null>(null)
  const [sortDirection, setSortDirection] =
    useState<DataTableSortDirection>('asc')

  const handleSort = (columnId: string): void => {
    const column = columnId as T
    if (sortColumnId !== column) {
      setSortColumnId(column)
      setSortDirection('asc')
    } else if (sortDirection === 'asc') {
      setSortDirection('desc')
    } else {
      setSortColumnId(null)
    }
    setCurrentPage(1)
  }

  const onPageSizeChange = (size: number): void => {
    setPageSize(size)
    setCurrentPage(1)
  }

  return {
    currentPage,
    pageSize,
    sortColumnId,
    sortDirection,
    handleSort,
    setCurrentPage,
    onPageSizeChange,
  }
}

export type AddDeviceProps = {
  initialDevice: IClinicPhoneDevice | null
  onCancel: () => void
  onSaved: () => void
}

const AddDevice = ({
  initialDevice,
  onCancel,
  onSaved,
}: AddDeviceProps): React.ReactElement => {
  const { teams } = useTeamCatalog()
  const hooks = useAddDevice({ initialDevice, onCancel, onSaved })
  const leaveGuard = useCommunicationsLeaveGuard(hooks.hasUnsavedChanges())
  const { baseline } = hooks
  const baselineCallRecordingMode = callRecordingModeFromBooleans(
    baseline.record_inbound_calls,
    baseline.record_outbound_calls,
  )
  const {
    currentPage,
    pageSize,
    sortColumnId,
    sortDirection,
    handleSort,
    setCurrentPage,
    onPageSizeChange,
  } = useSortedPage<E911SortColumn>()

  const isSavedDevice = !!hooks.deviceData.clinic_phone_device_id
  const e911Address = hooks.savedE911
  const e911RowId = e911Address?.clinic_e911_address_id || 'linked-e911'
  const e911AddressText = getFullAddress({
    address: e911Address?.address,
    city: e911Address?.city,
    state: e911Address?.state,
    zip: e911Address?.zip,
  })
  const e911IsDefault = e911Address?.is_default ? 'Yes' : 'No'
  const e911Status = e911Address?.status ?? ''

  const e911Rows: E911TableRow[] = [{ id: e911RowId }]
  const e911TotalPages = Math.max(1, Math.ceil(e911Rows.length / pageSize))
  const e911PageNumber = Math.min(currentPage, e911TotalPages)
  const e911PageRows = e911Rows.slice(
    (e911PageNumber - 1) * pageSize,
    e911PageNumber * pageSize,
  )

  const e911Columns: DataTableColumn<E911TableRow>[] = [
    {
      id: 'address',
      label: 'Address',
      sortable: true,
      width: '58%',
      renderCell: () => e911AddressText,
    },
    {
      id: 'is_default',
      label: 'Default emergency address',
      sortable: true,
      width: '19%',
      renderCell: () => (
        <Badge
          mode="tinted"
          color={e911IsDefault === 'Yes' ? 'vivid-green' : 'red'}
          label={e911IsDefault}
        />
      ),
    },
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      width: '13%',
      renderCell: () => (
        <Badge
          mode="tinted"
          color={E911_STATUS_COLOR[e911Status.toLowerCase()] ?? 'vivid-green'}
          label={e911Status}
        />
      ),
    },
  ]

  useEffect(() => {
    const title = (
      <Breadcrumbs
        items={[
          {
            label: 'Devices',
            onClick: () => void hooks.onClickCancel(),
          },
          { label: initialDevice ? 'Edit device' : 'Add device' },
        ]}
        showBackIcon
        onBackClick={() => void hooks.onClickCancel()}
      />
    )
    hooks.setHeaderTitle(title)
    hooks.setHeaderButtons(
      <>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => void hooks.onClickCancel()}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          variant="primary"
          onClick={() => void hooks.handleSave()}
          disabled={hooks.saving}
        >
          {hooks.saving ? 'Saving…' : 'Save changes'}
        </Button>
      </>,
    )
    return () => {
      hooks.setHeaderTitle(null)
      hooks.setHeaderButtons(null)
    }
  }, [
    hooks.handleSave,
    initialDevice,
    hooks.onClickCancel,
    hooks.saving,
    hooks.setHeaderButtons,
    hooks.setHeaderTitle,
  ])

  return (
    <>
      <div className="scp-d-add-device" id="settings-add-device">
        <div className="scp-pm-grid2">
          <InputField
            variant="text"
            fullWidth
            label="Device name"
            clearable={false}
            className={settingsFieldClassName(
              isSettingFieldChanged(
                hooks.deviceData.device_name,
                baseline.device_name,
              ),
            )}
            value={hooks.deviceData.device_name}
            onChange={(e) => hooks.handleChange('device_name', e.target.value)}
            helperText={hooks.validationErrors.device_name || undefined}
            error={!!hooks.validationErrors.device_name}
          />
          <Select
            label="Device type"
            className={settingsFieldClassName(
              isSettingFieldChanged(
                hooks.deviceData.device_type,
                baseline.device_type,
              ),
            )}
            value={hooks.deviceData.device_type}
            disabled={!!initialDevice}
            error={!!hooks.validationErrors.device_type}
            helperText={hooks.validationErrors.device_type || undefined}
            options={DEVICE_TYPES.map((type) => ({
              label: type.text,
              value: type.id,
            }))}
            onChange={(value) =>
              hooks.handleChange(
                'device_type',
                selectString(value) || 'soft_phone',
              )
            }
          />
          <Select
            label="Team"
            placeholder="Select team"
            className={settingsFieldClassName(
              isSettingFieldChanged(
                hooks.deviceData.team ?? '',
                baseline.team ?? '',
              ),
            )}
            value={
              hooks.deviceData.team != null &&
              String(hooks.deviceData.team).trim() !== ''
                ? hooks.deviceData.team
                : undefined
            }
            error={!!hooks.validationErrors.team}
            helperText={hooks.validationErrors.team || undefined}
            options={teams.map((team) => ({
              label: team,
              value: team,
            }))}
            onChange={(value) =>
              hooks.handleChange('team', selectString(value))
            }
          />
        </div>

        <div className="scp-pm-grid2">
          <Select
            label="Assigned to"
            placeholder="Select user"
            className={settingsFieldClassName(
              isSettingFieldChanged(
                hooks.deviceData.assigned_user_id ?? '',
                baseline.assigned_user_id ?? '',
              ),
            )}
            value={hooks.deviceData.assigned_user_id || undefined}
            error={!!hooks.validationErrors.assigned_user_id}
            helperText={hooks.validationErrors.assigned_user_id || undefined}
            options={hooks.userOptions.map((user) => ({
              label: user.label,
              value: user.user_id,
            }))}
            onChange={(value) =>
              hooks.handleChange('assigned_user_id', selectString(value))
            }
          />
          <Combobox
            label="Extension number"
            placeholder="100-900"
            allowCustom
            className={settingsFieldClassName(
              isSettingFieldChanged(
                hooks.deviceData.device_extension_number ?? '',
                baseline.device_extension_number ?? '',
              ),
            )}
            value={hooks.deviceData.device_extension_number || ''}
            error={!!hooks.validationErrors.device_extension_number}
            helperText={
              hooks.validationErrors.device_extension_number || undefined
            }
            options={hooks.extensionOptions.map((extension) => ({
              label: extension,
              value: extension,
            }))}
            onChange={(value) =>
              hooks.handleChange('device_extension_number', value)
            }
          />
          <MultiselectCombobox
            label="Call recording"
            placeholder="No"
            filterable={false}
            className={settingsFieldClassName(
              isSettingFieldChanged(
                hooks.callRecordingMode,
                baselineCallRecordingMode,
              ),
            )}
            options={CALL_RECORDING_OPTIONS.map((option) => ({
              label: option.text,
              value: option.id,
            }))}
            value={callRecordingModeToValues(hooks.callRecordingMode)}
            onChange={(values) =>
              hooks.handleCallRecordingChange(valuesToCallRecordingMode(values))
            }
          />
        </div>

        {(hooks.deviceData.device_type === 'desk_phone' ||
          hooks.deviceData.device_type === 'mobile_phone') && (
          <div className="scp-pm-grid2">
            <InputField
              variant="text"
              fullWidth
              label="PSTN number"
              clearable={false}
              className={settingsFieldClassName(
                isSettingFieldChanged(
                  hooks.deviceData.pstn_number ?? '',
                  baseline.pstn_number ?? '',
                ),
              )}
              value={hooks.deviceData.pstn_number ?? ''}
              onChange={(e) =>
                hooks.handleChange('pstn_number', e.target.value)
              }
              placeholder="+15555550100"
              helperText={hooks.validationErrors.pstn_number || undefined}
              error={!!hooks.validationErrors.pstn_number}
            />
          </div>
        )}

        {hooks.deviceData.device_type === 'voip_phone' && isSavedDevice && (
          <div className="scp-pm-grid2">
            <InputField
              variant="text"
              fullWidth
              label="SIP URI"
              value={hooks.deviceData.sip_uri ?? ''}
              disabled
              clearable={false}
            />
            <div className="scp-pm-field">
              <span className="scp-pm-label">SIP credentials</span>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => void hooks.regenerateSip()}
                disabled={hooks.sipBusy}
              >
                {hooks.sipBusy ? 'Regenerating…' : 'Regenerate SIP credentials'}
              </Button>
            </div>
          </div>
        )}

        <div className="scp-table-group">
          <span className="scp-table-title">
            E911 address
            <button
              type="button"
              className="scp-devices-info-btn ds-section-card__icon-button"
              aria-label="E911 address information"
              title="Only one E911 address can be linked to each device. The address is for emergency-call routing."
            >
              <InfoIcon aria-hidden="true" />
            </button>
          </span>
          {hooks.e911Linked ? (
            <DataTable<E911TableRow>
              rows={e911PageRows}
              columns={e911Columns}
              actionsLabel="Actions"
              renderActions={() => (
                <ActionButtons
                  actions={[
                    {
                      id: 'edit',
                      icon: (
                        <EditIcon
                          className="ds-action-button__svg"
                          aria-hidden="true"
                        />
                      ),
                      label: 'Edit',
                      tone: 'primary',
                      variant: 'outline',
                      onClick: () => hooks.setShowE911Modal(true),
                    },
                    {
                      id: 'delete',
                      icon: (
                        <Delete2Icon
                          className="ds-action-button__svg"
                          aria-hidden="true"
                        />
                      ),
                      label: 'Delete',
                      tone: 'danger',
                      variant: 'outline',
                      onClick: () => hooks.setPendingRemoveE911(true),
                    },
                  ]}
                />
              )}
              sortColumnId={sortColumnId ?? undefined}
              sortDirection={sortColumnId ? sortDirection : undefined}
              onSort={handleSort}
              pagination={{
                currentPage: e911PageNumber,
                totalPages: e911TotalPages,
                pageSize,
                onPageChange: setCurrentPage,
                onPageSizeChange,
              }}
            />
          ) : (
            <EmptyState
              title="No E911 address added"
              illustration={<LocationIcon aria-hidden="true" />}
              description={
                isSavedDevice
                  ? 'There is no E911 address added for this device.'
                  : 'E911 address can be added after adding device info.'
              }
              actionLabel={isSavedDevice ? 'Add E911 address' : undefined}
              onAction={() => hooks.setShowE911Modal(true)}
            />
          )}
          {}
        </div>
      </div>

      {leaveGuard.modal}
      <UnsavedChangesModal
        show={hooks.showUnsavedChangesModal}
        onConfirm={() => void hooks.discardUnsavedChanges()}
        onHide={() => void hooks.onHide()}
      />

      {hooks.sipOnceModal && (
        <VoipConfigurationModal
          show={!!hooks.sipOnceModal}
          onHide={() => hooks.dismissSipOnceModal()}
          data={hooks.sipOnceModal}
        />
      )}

      <E911AddressModal
        show={hooks.showE911Modal}
        onHide={() => {
          hooks.setShowE911Modal(false)
          hooks.setE911Form(hooks.defaultE911Form)
        }}
        data={hooks.e911Form}
        setData={hooks.setE911Form}
        onSaved={() => void hooks.submitE911Flow()}
      />

      <Modal
        open={hooks.pendingRemoveE911}
        onClose={() => hooks.setPendingRemoveE911(false)}
        closeOnBackdrop={false}
        size="sm"
        variant="confirmation"
        title="Remove E911 address?"
        icon={<Delete2Icon width={16} height={16} aria-hidden="true" />}
        iconTone="danger"
        description="This removes the emergency address linked to this device."
        secondaryLabel="Cancel"
        primaryLabel="Delete"
        primaryDestructive
        onSecondaryClick={() => hooks.setPendingRemoveE911(false)}
        onPrimaryClick={() => void hooks.removeE911FromDevice()}
      />
    </>
  )
}

export default withTeamCatalog(AddDevice)
